import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API, useAuth } from '../App';
import { useT } from '../i18n';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t } = useT();
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [detail, setDetail] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setDetail(t('Missing invite token'));
      return;
    }

    if (!isAuthenticated) {
      setStatus('error');
      setDetail(t('Please sign in first, then open the invite link again.'));
      return;
    }

    let cancelled = false;

    const accept = async () => {
      try {
        await axios.post(`${API}/workspace/accept/${token}`);
        if (cancelled) return;
        setStatus('success');
        toast.success(t('Invitation accepted!'));
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        setDetail(err?.response?.data?.detail || t('Failed to accept invite'));
        toast.error(err?.response?.data?.detail || t('Failed to accept invite'));
      }
    };

    accept();
    return () => { cancelled = true; };
  }, [token, isAuthenticated, t]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-8">
      <Card className="w-full max-w-md">
        <CardContent className="py-10 text-center space-y-4">
          {status === 'loading' && (
            <>
              <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
              <p className="text-muted-foreground">{t('Accepting invitation…')}</p>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle className="mx-auto h-10 w-10 text-green-500" />
              <p className="font-semibold">{t('Invitation accepted!')}</p>
              <Button onClick={() => navigate('/workspace')}>{t('Go to Workspace')}</Button>
            </>
          )}
          {status === 'error' && (
            <>
              <XCircle className="mx-auto h-10 w-10 text-destructive" />
              <p className="font-semibold">{t('Could not accept invitation')}</p>
              {detail && <p className="text-sm text-muted-foreground">{detail}</p>}
              <Button variant="outline" onClick={() => navigate('/auth')}>{t('Sign in')}</Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
